import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchOutliers,
  relinkListings,
  type OutlierBucket,
  type OutlierRow
} from '$lib/api/adminOutliers';

// Operator-facing outliers triage page. Surfaces likely-wrong-linked
// listings via three buckets, lets the operator multi-select rows, and
// re-links via the existing strict-link migrate endpoint with
// auto_flip=true.
//
// Match the austere palette used by AdminScopePage — no design polish.
// This is not a customer-facing surface.

const BUCKETS: { id: OutlierBucket; label: string; hint: string }[] = [
  {
    id: 'high',
    label: 'High kr/m²',
    hint: '> p99 of in-scope rentals (likely small Unit, big listing)'
  },
  {
    id: 'low',
    label: 'Low kr/m²',
    hint: '< p01 of in-scope rentals (likely big Unit, small listing)'
  },
  {
    id: 'disagreement',
    label: 'm² disagreement',
    hint: 'BBR disagreement events on m² / rooms / construction year'
  }
];

export function AdminOutliersPage() {
  const qc = useQueryClient();
  const [bucket, setBucket] = useState<OutlierBucket>('high');
  // Selection is keyed by listing_id, scoped per-bucket so switching
  // tabs doesn't accidentally re-link rows the operator can no longer
  // see.
  const [selected, setSelected] = useState<Record<OutlierBucket, Set<string>>>({
    high: new Set(),
    low: new Set(),
    disagreement: new Set()
  });
  const [relinkResult, setRelinkResult] = useState<{
    jobId: string;
    detail: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const outliersQuery = useQuery({
    queryKey: ['admin', 'outliers', bucket],
    queryFn: () => fetchOutliers(bucket, 50)
  });

  const relinkMutation = useMutation({
    mutationFn: (ids: string[]) => relinkListings(ids),
    onSuccess: (resp) => {
      setRelinkResult({ jobId: resp.job_id, detail: resp.detail });
      setErrorMsg(null);
      // Clear selection for the bucket we just submitted; re-fetch the
      // table so already-flipped rows fall out once the worker finishes
      // (the operator usually polls manually — no auto-poll here).
      setSelected((prev) => ({ ...prev, [bucket]: new Set() }));
      qc.invalidateQueries({ queryKey: ['admin', 'outliers'] });
    },
    onError: (err: Error) => {
      setErrorMsg(err.message || 'Failed to enqueue re-link');
      setRelinkResult(null);
    }
  });

  const items = useMemo(
    () => outliersQuery.data?.items ?? [],
    [outliersQuery.data]
  );
  const currentSelection = selected[bucket];
  const selectedCount = currentSelection.size;

  const toggleRow = (listingId: string) => {
    setSelected((prev) => {
      const next = new Set(prev[bucket]);
      if (next.has(listingId)) next.delete(listingId);
      else next.add(listingId);
      return { ...prev, [bucket]: next };
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next =
        prev[bucket].size === items.length
          ? new Set<string>()
          : new Set<string>(items.map((i) => i.listing_id));
      return { ...prev, [bucket]: next };
    });
  };

  const submitRelink = () => {
    if (currentSelection.size === 0) return;
    setRelinkResult(null);
    setErrorMsg(null);
    relinkMutation.mutate(Array.from(currentSelection));
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <header className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3">
        <h1 className="text-md font-semibold text-fg">Outliers</h1>
        <span className="text-xs text-fg-muted">
          Likely wrong-linked listings — pick rows, re-link via strict-link.
        </span>
      </header>

      <nav
        className="flex shrink-0 items-end gap-1 border-b border-rule bg-surface-2 px-4"
        data-testid="admin-outliers-tabs"
      >
        {BUCKETS.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => setBucket(b.id)}
            data-testid={`admin-outliers-tab-${b.id}`}
            className={
              'border-b-2 px-3 py-2 text-xs ' +
              (bucket === b.id
                ? 'border-fg font-medium text-fg'
                : 'border-transparent text-fg-muted hover:text-fg')
            }
          >
            {b.label}
            {outliersQuery.data && bucket === b.id && (
              <span className="ml-2 text-fg-muted">
                ({outliersQuery.data.count})
              </span>
            )}
          </button>
        ))}
      </nav>

      <p className="px-4 pt-3 text-xs text-fg-muted">
        {BUCKETS.find((b) => b.id === bucket)?.hint}
        {bucket === 'high' && outliersQuery.data?.p99 != null && (
          <span className="ml-2 font-mono">
            p99 = {outliersQuery.data.p99.toFixed(1)} kr/m²
          </span>
        )}
        {bucket === 'low' && outliersQuery.data?.p01 != null && (
          <span className="ml-2 font-mono">
            p01 = {outliersQuery.data.p01.toFixed(1)} kr/m²
          </span>
        )}
      </p>

      <section className="flex-1 overflow-x-auto p-4">
        {outliersQuery.isPending && (
          <div className="text-xs text-fg-muted">Loading…</div>
        )}
        {outliersQuery.isError && (
          <div className="text-xs text-red-600 dark:text-red-400" role="alert">
            Failed to load outliers.
          </div>
        )}
        {outliersQuery.data && (
          <table
            className="w-full border-collapse text-sm"
            data-testid="admin-outliers-table"
          >
            <thead>
              <tr className="border-b border-rule text-left text-xs uppercase tracking-wide text-fg-muted">
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={
                      items.length > 0 && selectedCount === items.length
                    }
                    aria-label="Select all"
                    onChange={toggleAll}
                    data-testid="admin-outliers-select-all"
                  />
                </th>
                <th className="px-2 py-2">Listing address</th>
                <th className="px-2 py-2">Listing m² / rooms</th>
                <th className="px-2 py-2">Rent</th>
                <th className="px-2 py-2">Linked Unit</th>
                <th className="px-2 py-2">Unit m² / rooms</th>
                <th className="px-2 py-2">Outlier metric</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <OutlierTableRow
                  key={row.listing_id}
                  row={row}
                  selected={currentSelection.has(row.listing_id)}
                  onToggle={() => toggleRow(row.listing_id)}
                />
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-2 py-6 text-center text-xs text-fg-muted"
                  >
                    No outliers in this bucket.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      <footer className="flex shrink-0 items-center gap-3 border-t border-rule bg-surface-2 px-4 py-3">
        <button
          type="button"
          onClick={submitRelink}
          disabled={selectedCount === 0 || relinkMutation.isPending}
          data-testid="admin-outliers-relink-button"
          className="h-8 rounded bg-fg px-3 text-xs font-medium text-surface disabled:opacity-50"
        >
          {relinkMutation.isPending
            ? 'Submitting…'
            : `Re-link selected (${selectedCount})`}
        </button>
        {relinkResult && (
          <span
            className="text-xs text-fg-muted"
            data-testid="admin-outliers-relink-result"
          >
            Job <span className="font-mono text-fg">{relinkResult.jobId}</span>{' '}
            queued — {relinkResult.detail}
          </span>
        )}
        {errorMsg && (
          <span
            className="text-xs text-red-600 dark:text-red-400"
            role="alert"
          >
            {errorMsg}
          </span>
        )}
      </footer>
    </div>
  );
}

interface OutlierTableRowProps {
  row: OutlierRow;
  selected: boolean;
  onToggle: () => void;
}

function OutlierTableRow({ row, selected, onToggle }: OutlierTableRowProps) {
  const snapAddr = row.snapshot.address_raw ?? '—';
  const snapPostalCity = [row.snapshot.postal_code, row.snapshot.city]
    .filter(Boolean)
    .join(' ');
  const unitArea = row.unit.area_m2 ?? null;
  const unitRooms = row.unit.rooms ?? null;
  const metric =
    row.metric_kr_per_m2 != null
      ? `${row.metric_kr_per_m2.toFixed(0)} kr/m²`
      : row.disagreement_fields && row.disagreement_fields.length > 0
      ? row.disagreement_fields.join(', ')
      : '—';

  return (
    <tr
      className="border-b border-rule"
      data-testid={`admin-outliers-row-${row.listing_id}`}
    >
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select listing ${row.listing_id}`}
          data-testid={`admin-outliers-checkbox-${row.listing_id}`}
        />
      </td>
      <td className="px-2 py-2 text-fg">
        <div className="truncate max-w-[20rem]" title={snapAddr}>
          {snapAddr}
        </div>
        {snapPostalCity && (
          <div className="text-2xs text-fg-muted">{snapPostalCity}</div>
        )}
      </td>
      <td className="px-2 py-2 font-mono text-xs text-fg">
        {row.snapshot.area_m2 ?? '—'} m² / {row.snapshot.rooms ?? '—'} r
      </td>
      <td className="px-2 py-2 font-mono text-xs text-fg">
        {row.snapshot.monthly_rent_dkk != null
          ? `${row.snapshot.monthly_rent_dkk.toLocaleString()} kr`
          : '—'}
      </td>
      <td className="px-2 py-2 text-xs text-fg-muted">
        <div className="truncate max-w-[18rem]" title={row.unit.address ?? ''}>
          {row.unit.address ?? '—'}
        </div>
        <div className="text-2xs font-mono text-fg-muted">
          BBR {row.unit.bbr_unit_id ?? '—'}
        </div>
      </td>
      <td className="px-2 py-2 font-mono text-xs text-fg">
        {unitArea ?? '—'} m² / {unitRooms ?? '—'} r
      </td>
      <td className="px-2 py-2 font-mono text-xs text-fg">{metric}</td>
    </tr>
  );
}
